// The command set behind the interactive terminal in the hero.
// Each command returns React nodes, which the terminal prints as output.

import React from "react";
import { education, experience, profile, projects, skills } from "../data/site";

const Row = ({ k, v }) => (
  <span className="term__row">
    <b>{k}</b>
    <em>{v}</em>
  </span>
);

const listOf = (items) => items.join("  ·  ");

export const BOOT = [
  { cmd: "whoami", out: <>ouri — senior software developer, <u>Brooklyn NY</u></> },
  { cmd: "cat stack.txt", out: <>C# · .NET Core · Azure · SQL Server · React · Swift</> },
  { cmd: "uptime", out: <>10+ years shipping production systems</> },
  {
    cmd: "help",
    out: (
      <>
        Try <u>skills</u>, <u>experience</u>, <u>projects</u>, <u>contact</u> — or{" "}
        <u>help</u> for everything. This prompt is live.
      </>
    ),
  },
];

const HELP = [
  ["whoami", "who you're talking to"],
  ["skills", "the full technical stack"],
  ["experience", "where I've worked"],
  ["projects", "things I've built"],
  ["education", "degrees and honors"],
  ["contact", "how to reach me"],
  ["resume", "download the PDF"],
  ["github", "open my GitHub"],
  ["linkedin", "open my LinkedIn"],
  ["theme", "flip dark / light"],
  ["date", "current date and time"],
  ["clear", "wipe the screen"],
];

/**
 * Builds the command table. Takes the side effects it needs (navigation,
 * theme, clearing) so the commands stay pure data.
 */
/** Returned by `clear` to tell the terminal to drop the whole buffer,
 *  including the `clear` line itself — the way a real shell behaves. */
export const CLEAR = Symbol("clear");

export function createCommands({ navigate, toggleTheme }) {
  return {
    help: () => (
      <span className="term__grid">
        {HELP.map(([name, desc]) => (
          <Row key={name} k={name} v={desc} />
        ))}
      </span>
    ),

    whoami: () => (
      <>
        {profile.name} — {profile.role}, currently at {profile.current}.
        <br />
        {profile.tagline}
      </>
    ),

    skills: () => (
      <span className="term__grid">
        {skills.map((group) => (
          <Row key={group.group} k={group.group.toLowerCase()} v={listOf(group.items)} />
        ))}
      </span>
    ),

    stack: (args, cmds) => cmds.skills(),

    experience: () => (
      <span className="term__grid">
        {experience.map((job) => (
          <Row key={job.id} k={job.period.split(" — ")[0]} v={`${job.role} @ ${job.company}`} />
        ))}
        <span className="term__note">
          Run <u>open resume</u> for the full detail.
        </span>
      </span>
    ),

    projects: () => (
      <span className="term__grid">
        {projects.map((project) => (
          <Row key={project.id} k={project.tags[0].toLowerCase()} v={project.title} />
        ))}
      </span>
    ),

    education: () => (
      <span className="term__grid">
        {education.map((item) => (
          <Row key={item.id} k={item.period} v={`${item.degree} — ${item.gpa}`} />
        ))}
      </span>
    ),

    contact: () => (
      <span className="term__grid">
        <Row k="email" v={profile.email} />
        <Row k="location" v={profile.location} />
        <Row k="github" v="github.com/ourialkada" />
        <Row k="linkedin" v="linkedin.com/in/ouri-alkada" />
      </span>
    ),

    resume: () => {
      window.open(profile.resume, "_blank", "noopener");
      return <>opening résumé…</>;
    },

    github: () => {
      window.open("https://github.com/ourialkada", "_blank", "noopener");
      return <>opening github.com/ourialkada…</>;
    },

    linkedin: () => {
      window.open("https://www.linkedin.com/in/ouri-alkada-46267a109/", "_blank", "noopener");
      return <>opening LinkedIn…</>;
    },

    theme: () => {
      toggleTheme();
      return <>theme flipped.</>;
    },

    date: () => <>{new Date().toString()}</>,

    open: (args) => {
      const target = (args[0] || "").toLowerCase();
      const routes = {
        resume: "/resume",
        projects: "/projects",
        contact: "/contact",
        home: "/",
      };
      if (routes[target]) {
        navigate(routes[target]);
        return <>navigating to {routes[target]}…</>;
      }
      return <>usage: open [home|resume|projects|contact]</>;
    },

    ls: () => <>resume/ &nbsp; projects/ &nbsp; skills/ &nbsp; contact/ &nbsp; stack.txt</>,

    echo: (args) => <>{args.join(" ")}</>,

    clear: () => CLEAR,

    sudo: () => <>nice try. you already have root here.</>,

    exit: () => <>there is no escape. try <u>open home</u>.</>,
  };
}

export const COMMAND_NAMES = (cmds) => Object.keys(cmds).sort();
